import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { appVersion } from "@/core/appVersion";
import welcomeIllustration from "@/assets/froppio-welcome.svg";
import { Brand } from "@/shared/components/ui/Brand";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";

const legalDocuments = [
  { id: "privacy", title: "Privacy policy", description: "How your information is handled" },
  { id: "terms", title: "Terms of service", description: "The terms for using Froppio" },
  { id: "licenses", title: "Open-source licenses", description: "The software behind Froppio" },
];

export function AboutPage() {
  const { hash, pathname } = useLocation();
  const documentId = hash.slice(1);
  const legalDocument = legalDocuments.find(({ id }) => id === documentId);
  const content = useRef<HTMLDivElement>(null);

  useEffect(() => {
    content.current?.focus();
  }, [hash]);

  return (
    <Page>
      <PageHeader title={legalDocument?.title ?? (documentId ? "Page not found" : "About")} />
      <PageContent className="max-w-[620px]">
        <div ref={content} tabIndex={-1} className="space-y-6 outline-none">
          {documentId ? (
            <EmptyState
              title={legalDocument ? "Document not available yet" : "Page not found"}
              message={legalDocument
                ? `${legalDocument.title} content and an approved published link have not been provided. This page does not contain a legal document.`
                : "There is no About page at this address."}
              action={{ label: "About Froppio", to: pathname, variant: "outline" }}
            />
          ) : (
            <>
              <section aria-label="Froppio" className="grid justify-items-center gap-[18px] pb-3 pt-6 text-center">
                <Brand wordmark className="gap-2.5 [&>span:first-child]:text-[52px] [&>span:last-child]:text-[40px]" />
                <img
                  src={welcomeIllustration}
                  alt="A happy Froppio waving hello"
                  width={280}
                  height={240}
                  className="h-auto w-[200px] max-w-full"
                />
                <p className="text-sm text-muted-foreground">Version {appVersion}</p>
              </section>
              <Section title="Legal">
                <div className="space-y-2">
                  {legalDocuments.map(({ id, title, description }) => (
                    <InfoRow
                      key={id}
                      title={title}
                      subtitle={description}
                      caption="Document not available yet"
                      href={`#${id}`}
                    />
                  ))}
                </div>
              </Section>
            </>
          )}
        </div>
      </PageContent>
    </Page>
  );
}
